import { useEffect, useRef } from "react";

// useBackgroundPlayback — keep music playing reliably even when the
// phone screen is off, and surface a fully-functional lock-screen
// widget (play, pause, fast-forward, rewind, next, previous, scrub).
//
// 1) Acquires a Screen Wake Lock so the phone's display doesn't sleep
//    while a track is active (mobile browsers pause iframe audio when
//    the screen turns off).
// 2) Publishes track metadata to the Media Session API so the OS
//    lock-screen, Bluetooth car controls, and AirPods/Pixel Buds show
//    the current title/artist and the user can hit play/pause/seek/
//    next/prev from outside the browser.
// 3) Re-issues a YT `playVideo` postMessage whenever the tab becomes
//    visible again — mobile Chrome/Safari often pause embedded YT
//    iframes on tab-hide and the user expects them to resume.
// 4) Polls the position-state callback so the lock-screen widget
//    shows a live progress bar and the user can scrub from there too.
//
// SAFETY: All browser APIs we touch are feature-detected; missing
// support is silently ignored so the hook is a no-op on Firefox
// desktop / older mobiles.
export default function useBackgroundPlayback({
  active,
  track,
  iframeRef,
  onNext,
  onPrev,
  onPause,
  onPlay,
  // Fast-forward / rewind. `seconds` is how many seconds the OS
  // suggests (defaults to 10) so we can honor "rewind 10s" buttons
  // on Bluetooth car stereos and Android/iOS lock screens.
  onSeekForward,
  onSeekBackward,
  // Direct scrub from the lock-screen progress bar. `seconds` is the
  // absolute position requested by the OS.
  onSeekTo,
  // Returns {duration, position, playbackRate} or null. Called every
  // ~1s while playing to update the lock-screen progress indicator.
  getPositionState,
}) {
  const wakeLockRef = useRef(null);

  // Universal "one voice at a time" bridge. When THIS player goes
  // active, fire a global event so any other audio surface (Bible
  // read-aloud, background gospel mix, sermon player, etc.) stops
  // itself. When ANOTHER player broadcasts the same event, the
  // consumer of this hook gets a callback so it can pause itself.
  // This is the belt-and-suspenders fix for the "double voices" bug
  // — even brand new players get the behavior for free as long as
  // they call this hook.
  useEffect(() => {
    if (!active) return;
    try {
      window.dispatchEvent(new CustomEvent("hw:audio:start", { detail: { id: track?.id } }));
    } catch (_) {}
  }, [active, track?.id]);
  useEffect(() => {
    const onOther = (e) => {
      // Ignore our own broadcast.
      if (e?.detail?.id && track?.id && e.detail.id === track.id) return;
      if (!active) return;
      // Pause ourselves via the supplied callback. Falls back to a
      // direct YT postMessage if the caller didn't provide onPause.
      if (typeof onPause === "function") onPause();
      else if (iframeRef?.current?.contentWindow) {
        try {
          iframeRef.current.contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
        } catch (_) {}
      }
    };
    window.addEventListener("hw:audio:start", onOther);
    window.addEventListener("hw:bgmusic:start", onOther);
    return () => {
      window.removeEventListener("hw:audio:start", onOther);
      window.removeEventListener("hw:bgmusic:start", onOther);
    };
  }, [active, track?.id, onPause, iframeRef]);

  // Acquire / release Screen Wake Lock based on `active`.
  useEffect(() => {
    if (typeof window === "undefined" || !("wakeLock" in navigator)) return;
    let cancelled = false;
    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { lock.release(); } catch (_) {}
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener?.("release", () => { wakeLockRef.current = null; });
      } catch (_) { /* permission denied / unsupported — ignore */ }
    };
    const release = () => {
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch (_) {}
        wakeLockRef.current = null;
      }
    };
    if (active) acquire(); else release();
    // Re-acquire after the tab becomes visible again (the OS releases
    // wake locks on visibilitychange to hidden).
    const onVis = () => {
      if (active && document.visibilityState === "visible" && !wakeLockRef.current) acquire();
      // Also try to resume the YouTube iframe — mobile browsers pause
      // embedded YT players when the tab is backgrounded.
      if (active && document.visibilityState === "visible" && iframeRef?.current?.contentWindow) {
        try {
          iframeRef.current.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
        } catch (_) {}
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      release();
    };
  }, [active, iframeRef]);

  // Publish metadata + action handlers to the Media Session API. This
  // is what makes the lock-screen show "Now Playing: <title> · <artist>"
  // with working play / pause / next / previous / seek buttons.
  useEffect(() => {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) return;
    if (!active || !track) {
      try { navigator.mediaSession.metadata = null; } catch (_) {}
      return;
    }
    try {
      const title = track.title || track.deceased_name || "Holy Words";
      const artist = track.artist || track.speaker || track.teacher || track.creator || "Holy Words";
      const album = "Holy Words";
      const artUrl = track.thumbnail_url
        || (track.youtube_id ? `https://i.ytimg.com/vi/${track.youtube_id}/hqdefault.jpg` : null);
      const artwork = artUrl ? [
        { src: artUrl, sizes: "480x360", type: "image/jpeg" },
      ] : [];
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new window.MediaMetadata({ title, artist, album, artwork });
      navigator.mediaSession.setActionHandler?.("play", () => { onPlay && onPlay(); });
      navigator.mediaSession.setActionHandler?.("pause", () => { onPause && onPause(); });
      navigator.mediaSession.setActionHandler?.("nexttrack", () => { onNext && onNext(); });
      navigator.mediaSession.setActionHandler?.("previoustrack", () => { onPrev && onPrev(); });
      // Fast-forward / rewind. OS may pass a seekOffset (seconds) — we
      // honor it but fall back to ±15s if the OS doesn't supply one.
      navigator.mediaSession.setActionHandler?.("seekforward", (e) => {
        const delta = (e && e.seekOffset) || 15;
        onSeekForward && onSeekForward(delta);
      });
      navigator.mediaSession.setActionHandler?.("seekbackward", (e) => {
        const delta = (e && e.seekOffset) || 15;
        onSeekBackward && onSeekBackward(delta);
      });
      // Scrub from the lock-screen progress bar. e.seekTime is an
      // absolute position in seconds; fastSeek is a perf hint we can
      // mostly ignore on YT iframes.
      navigator.mediaSession.setActionHandler?.("seekto", (e) => {
        if (e && typeof e.seekTime === "number") {
          onSeekTo && onSeekTo(e.seekTime);
        }
      });
    } catch (_) { /* unsupported MediaMetadata constructor on older Safari — ignore */ }

    // Clean up handlers when this effect re-runs so we don't leak
    // stale closures referencing an old track.
    return () => {
      ["play", "pause", "nexttrack", "previoustrack", "seekforward", "seekbackward", "seekto"].forEach((action) => {
        try { navigator.mediaSession.setActionHandler?.(action, null); } catch (_) {}
      });
    };
  }, [active, track, onNext, onPrev, onPause, onPlay, onSeekForward, onSeekBackward, onSeekTo]);

  // Drive the lock-screen progress bar. setPositionState lets the OS
  // show a real seek bar with elapsed / remaining time AND lets the
  // user drag it — at which point our "seekto" handler above fires.
  useEffect(() => {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) return;
    if (!active || !getPositionState) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      try {
        const s = getPositionState();
        if (s && Number.isFinite(s.duration) && s.duration > 0 && Number.isFinite(s.position)) {
          navigator.mediaSession.setPositionState({
            duration: s.duration,
            position: Math.max(0, Math.min(s.position, s.duration)),
            playbackRate: s.playbackRate || 1.0,
          });
        }
      } catch (_) { /* setPositionState throws on bad input — silent */ }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [active, getPositionState]);
}
